import sampleCsvUrl from "@/assets/SampleCampaigns.csv?url";
import type { CampaignCsv } from "@/types/campaign";
import { CANONICAL_HEADERS, isKnownHeader } from "./campaignContactScan";

/**
 * One parsed CSV record plus the physical line it began on. The line is kept
 * because the payload's `row_number` refers to the row as it appears in the
 * user's file — that is what makes a backend validation error findable in the
 * spreadsheet they uploaded.
 */
interface CsvRecord {
  line: number;
  cells: string[];
}

/**
 * RFC 4180 CSV scanner. Hand-rolled because the repo has no CSV dependency and
 * a `split(",")` would corrupt the two things real exports contain: quoted
 * fields holding commas (`"Manchester, Greater Manchester"`) and quoted fields
 * holding newlines. Doubled quotes (`""`) are the escape for a literal quote.
 */
function parseCsvRecords(text: string): CsvRecord[] {
  const records: CsvRecord[] = [];
  let cells: string[] = [];
  let field = "";
  let inQuotes = false;
  let line = 1;
  let recordLine = 1;
  let started = false;

  const beginRecord = () => {
    if (started) return;
    started = true;
    recordLine = line;
  };

  const endRecord = () => {
    if (!started && !field && cells.length === 0) return; // blank line
    cells.push(field);
    records.push({ line: recordLine, cells });
    cells = [];
    field = "";
    started = false;
  };

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        if (ch === "\n") line += 1;
        field += ch;
      }
      continue;
    }

    if (ch === '"') {
      beginRecord();
      inQuotes = true;
    } else if (ch === ",") {
      beginRecord();
      cells.push(field);
      field = "";
    } else if (ch === "\r") {
      // Swallowed; the \n that follows ends the record.
    } else if (ch === "\n") {
      endRecord();
      line += 1;
      recordLine = line;
    } else {
      beginRecord();
      field += ch;
    }
  }
  endRecord();

  return records;
}

export interface CsvParseResult {
  /** Payload for the `csv` key of the create request, or null when unusable. */
  payload: CampaignCsv | null;
  /** Blocking problems, phrased so the user can fix the file. */
  errors: string[];
  headers: string[];
  rowCount: number;
  /** Headers flagged "Mandatory" on the first line. */
  mandatoryHeaders: string[];
}

const isMandatoryFlag = (flag: string | undefined) => (flag ?? "").trim().toLowerCase() === "mandatory";

/** Most data rows a single import may carry. */
export const MAX_CSV_ROWS = 100;

/**
 * Downloadable example file showing the layout an import has to follow. Bundled with
 * the app rather than fetched from storage, so the link saves the file the parser here
 * actually accepts instead of navigating away to whatever that bucket holds.
 */
export const SAMPLE_CSV_URL = sampleCsvUrl;

/** Filename the sample is saved under. */
export const SAMPLE_CSV_FILENAME = "SampleCampaigns.csv";

/** How many row-level problems of one kind are listed before they are summarised. */
const MAX_LISTED = 5;

/** The columns the file may use, for the "unknown column" message. */
const ALLOWED_COLUMNS = Object.values(CANONICAL_HEADERS).join(", ");

/** Adds up to {@link MAX_LISTED} problems, then a count of the rest. */
function pushCapped(errors: string[], problems: string[], more: (n: number) => string) {
  if (problems.length === 0) return;
  errors.push(...problems.slice(0, MAX_LISTED));
  if (problems.length > MAX_LISTED) errors.push(more(problems.length - MAX_LISTED));
}

/**
 * Turns raw CSV text into the `csv` payload.
 *
 * The file's shape is fixed: line 1 holds the per-column "Mandatory" flags,
 * line 2 holds the headers, and data starts on line 3 — which is why the
 * example payload's first row is `row_number: 3`.
 */
export function parseCampaignCsv(text: string): CsvParseResult {
  const empty: CsvParseResult = {
    payload: null,
    errors: [],
    headers: [],
    rowCount: 0,
    mandatoryHeaders: [],
  };

  // Excel writes a UTF-8 BOM; left in place it becomes part of the first header.
  const records = parseCsvRecords(text.replace(/^\uFEFF/, ""));

  if (records.length < 3) {
    return {
      ...empty,
      errors: [
        "The file needs a flags row, a header row and at least one data row (data starts on line 3).",
      ],
    };
  }

  const [flagsRecord, headerRecord, ...dataRecords] = records;
  const headers = headerRecord.cells.map((h) => h.trim());
  const errors: string[] = [];

  if (headers.some((h) => !h)) errors.push("Every column on the header row needs a name.");

  const duplicates = headers.filter((h, i) => h && headers.indexOf(h) !== i);
  if (duplicates.length > 0) {
    errors.push(`Duplicate column name(s): ${[...new Set(duplicates)].join(", ")}.`);
  }

  // The import endpoint answers a column it does not know with UNKNOWN_HEADER and
  // fails the whole file. Caught here so the user is told which column to drop
  // while they still have the file open, rather than after a round trip.
  const unknown = headers.filter((h) => h && !isKnownHeader(h));
  if (unknown.length > 0) {
    errors.push(
      `Unknown column${unknown.length === 1 ? "" : "s"}: ${unknown.join(", ")}. ` +
        `The file may only use these columns: ${ALLOWED_COLUMNS}.`,
    );
  }

  // A row carrying values past the last column means a stray comma or a value
  // with a comma in it that was never quoted. Those extra values are dropped
  // silently below, taking part of the row's data with them — so the file is
  // rejected instead. Empty overhang is ignored: a spreadsheet export routinely
  // leaves trailing commas on a row.
  const ragged = dataRecords
    .filter((record) => record.cells.slice(headers.length).some((cell) => cell.trim() !== ""))
    .map(
      (record) =>
        `Row ${record.line}: ${record.cells.length} values for ${headers.length} columns — check for an unquoted comma.`,
    );
  pushCapped(errors, ragged, (n) => `…and ${n} more row(s) with too many values.`);

  // Normalised to the header count so the two arrays always line up, however
  // many trailing commas the spreadsheet export left on the flags row.
  const mandatoryFlags = headers.map((_, i) => (isMandatoryFlag(flagsRecord.cells[i]) ? "Mandatory" : ""));
  const mandatoryHeaders = headers.filter((_, i) => mandatoryFlags[i] === "Mandatory");

  const rows = dataRecords.map((record) => ({
    row_number: record.line,
    values: Object.fromEntries(headers.map((header, i) => [header, (record.cells[i] ?? "").trim()])),
  }));

  // Checked before the per-row work below so an oversized file fails fast with
  // one actionable message instead of a wall of row-level errors.
  if (rows.length > MAX_CSV_ROWS) {
    return {
      ...empty,
      errors: [
        `The file has ${rows.length} data rows. A maximum of ${MAX_CSV_ROWS} rows can be imported at a time.`,
      ],
      headers,
      rowCount: rows.length,
      mandatoryHeaders,
    };
  }

  // Mandatory columns are checked here rather than left to the backend, so a
  // bad file is reported per row instead of coming back as one opaque 400.
  const missing: string[] = [];
  for (const row of rows) {
    for (const header of mandatoryHeaders) {
      if (!row.values[header]) missing.push(`Row ${row.row_number}: "${header}" is required.`);
    }
  }
  pushCapped(errors, missing, (n) => `…and ${n} more required value(s) missing.`);

  if (rows.length === 0) errors.push("The file has no data rows.");

  return {
    payload:
      errors.length === 0
        ? { mandatory_flags: mandatoryFlags, headers, rows }
        : null,
    errors,
    headers,
    rowCount: rows.length,
    mandatoryHeaders,
  };
}
